import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import ModalComponent from '../ModalComponent';
import FixedAspectOptions from './components/FixedAspectOptions';
import DynamicAspectOptions from './components/DynamicAspectOptions';
import ImagePreview from './components/ImagePreview';
import ImageScaleSlider from './components/ImageScaleSlider';
import ToggleFixedAspect from './components/ToggleFixedAspect';
import ImageModal from '../ImageModal/ImageModal';
import { ImagesContext } from '../../../contexts/ImagesContext';
import ImageSettings, { Size } from '../../../models/BlockComponentSettings/ImageSettings';
import BlockImageListItemData from '../../Blocks/BlockImageList/BlockImageListItemData';
import { DynamicAspect, ImageAspectRatio, ImageOrientation } from '../../Blocks/BlockImageList/ImageSettings';

import LinkComponent from '../../Link/LinkComponent';
import BlockData from '../../../models/BlockData';
import { URLOpenType } from '../../../models/URLOpenType';
import LinkData from '../../../models/LinkData';
import { fitParent } from '../../../utils/image';

enum Crop {
  LANDSCAPE_16_9 = 'landscape-16:9',
  LANDSCAPE_4_3 = 'landscape-4:3',
  SQUARE = 'square-1:1',
  PORTRAIT_16_9 = 'portrait-16:9',
  PORTRAIT_4_3 = 'portrait-4:3',
}

const CropOrientedAspect: Record<Crop, Size> = {
  [Crop.LANDSCAPE_16_9]: { width: 533, height: 300 },
  [Crop.LANDSCAPE_4_3]: { width: 400, height: 300 },
  [Crop.SQUARE]: { width: 300, height: 300 },
  [Crop.PORTRAIT_4_3]: { width: 225, height: 300 },
  [Crop.PORTRAIT_16_9]: { width: 168, height: 300 },
};

interface ImageSettingsModalProps {
  imageListItem?: BlockImageListItemData;
  disabled: boolean;
  children: JSX.Element;
  blockData: BlockData[];
  onConfirm: (image: BlockImageListItemData) => void;
}

const ImageSettingsModal = ({
  imageListItem,
  disabled,
  children,
  blockData,
  onConfirm,
}: ImageSettingsModalProps) => {
  const { images } = useContext(ImagesContext);
  const [modalShow, setModalShow] = useState(false);
  const [item, setItem] = useState<BlockImageListItemData>(imageListItem);
  const [imageSize, setImageSize] = useState<Size>({ width: 0, height: 0 });

  // ITEM SETTINGS
  const handleChangeFixedAspect = (fixedAspect: boolean) => {
    if (fixedAspect !== null) {
      setItem({ ...item });
    }
  };
  const handleChangeDynamicAspect = () => {
    setItem({ ...item });
  };
  const handleChangeOrientation = () => {
    setItem({ ...item });
  };
  const handleChangeAspectRatio = () => {
    setItem({ ...item });
  };

  // IMAGE SETTINGS
  const handleChangeImage = (value: string) => {
    setItem({ ...item, image: { ...item.image, value } });
  };
  const handleChangeZoom = (scale: number) => {
    setItem({ ...item, image: { ...item.image, settings: { ...item.image.settings, scale } } });
  };
  const handleChangeImageSettings = (settings: ImageSettings) => {
    item.image.settings = settings;
  };
  const handleLinkEnable = (isEnabled: boolean) => {
    if (isEnabled) {
      setItem({ ...item, link: LinkData.getDefault() });
    } else {
      setItem({ ...item, link: undefined });
    }
  };

  const handleConfirm = () => {
    onConfirm(item);
    setModalShow(false);
  };

  const selectedAspectRatio = useMemo(() => {
    if (item.image.settings.fixedAspect) {
      if (item.image.settings.orientation === ImageOrientation.Square) return ImageAspectRatio.OneByOne;
      if (item.image.settings.aspectRatio === ImageAspectRatio.SixteenByNine) return ImageAspectRatio.SixteenByNine;
      if (item.image.settings.aspectRatio === ImageAspectRatio.FourByThree) return ImageAspectRatio.FourByThree;
    } else if (item.image.settings.dynamicAspect === DynamicAspect.Square) return ImageAspectRatio.OneByOne;

    return ImageAspectRatio.OneByOne;
  }, [item]);

  const selectedOrientation = useMemo(() => {
    if (item.image.settings.fixedAspect) return item.image.settings.orientation;
    if (item.image.settings.dynamicAspect === DynamicAspect.Square) return ImageOrientation.Square;

    return ImageOrientation.Landscape;
  }, [item]);

  const cropSize = useMemo(() => {
    if (!item.image.settings.fixedAspect && item.image.settings.dynamicAspect === DynamicAspect.Auto) {
      return fitParent({ width: 605, height: 340 }, imageSize, { width: 72, height: 40 });
    }
    return CropOrientedAspect[`${selectedOrientation}-${selectedAspectRatio}` as Crop];
  }, [imageSize, item.image.settings.dynamicAspect, item.image.settings.fixedAspect, selectedAspectRatio, selectedOrientation]);

  return (
    <>
      <Box
        sx={{
          height: '100%',
          maxHeight: '100%',
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={(event) => {
          if (disabled) return;
          setModalShow(true);
          event.stopPropagation();
        }}
      >
        {children}
      </Box>

      <ModalComponent
        size="xl"
        title="画像表示位置の設定"
        subTitle="このブロックで表示する画像の表示位置を調整することができます。"
        closeOnConfirm={false}
        show={modalShow}
        onConfirm={handleConfirm}
        closeModal={() => {
          setModalShow(false);
          setItem(imageListItem);
        }}
        cancelButtonText="リセット"
        confirmButtonText="保存"
      >
        <Grid container alignItems="start" sx={{ height: '100%' }}>
          <Grid container direction="column" item xs={8} justifyContent="start" wrap="nowrap" sx={{ height: '100%' }}>
            <Grid item container xs="auto" alignItems="start" justifyContent="start">
              <ImagePreview
                image={item.image}
                crop={cropSize}
                handleChange={handleChangeImageSettings}
                handleImageSizeChange={setImageSize}
              />
              <Box sx={{ mx: 1 }}>
                <ImageModal
                  images={images}
                  handleOnConfirm={handleChangeImage}
                  title="画像を選択"
                  absoluteButton={false}
                />
              </Box>
            </Grid>

            <Grid item xs="auto" sx={{ p: 1, width: 'auto' }}>
              <Grid container direction="column" justifyContent="start" sx={{ height: 'auto' }}>
                <FormControlLabel
                  label="この画像にリンクを設定する"
                  control={(
                    <Checkbox
                      checked={!!item.link}
                      onChange={(event) => handleLinkEnable(event.target.checked)}
                    />
                  )}
                />
                {item.link !== undefined && (
                  <LinkComponent
                    link={item.link}
                    blockData={blockData}
                    hideRemoveButton
                    hideTitle
                    onChangeLink={(url) => {
                      setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, url } } });
                    }}
                    onSelectBlock={(url) => {
                      setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, url } } });
                    }}
                    onChangeTargetType={(targetType) => {
                      setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, targetType } } });
                    }}
                    onChangeOpenType={(checked) => {
                      const openType = checked ? URLOpenType.NEW_TAB : URLOpenType.SAME_PAGE;
                      setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, openType } } });
                    }}
                  />
                )}
              </Grid>
            </Grid>

          </Grid>

          <Grid container item xs={4} justifyContent="center" sx={{ p: 2 }}>
            <ToggleFixedAspect
              value={item.image.settings.fixedAspect}
              handleChange={handleChangeFixedAspect}
            />

            <Divider sx={{ width: '100%', my: 1 }} />

            {item.image.settings.fixedAspect ? (
              <FixedAspectOptions
                orientation={item.image.settings.orientation}
                aspectRatio={item.image.settings.aspectRatio}
                handleChangeOrientation={handleChangeOrientation}
                handleChangeAspectRatio={handleChangeAspectRatio}
              />
            ) : (
              <DynamicAspectOptions value={item.image.settings.dynamicAspect} handleChange={handleChangeDynamicAspect} />
            )}

            <Divider sx={{ width: '100%', my: 1 }} />

            <ImageScaleSlider
              value={item.image.settings.scale}
              handleChange={handleChangeZoom}
            />

          </Grid>

        </Grid>
      </ModalComponent>
    </>
  );
};

export default ImageSettingsModal;
